import { derivePaymentStatus } from '../bookings/domain';
import type { Booking, BookingStatus } from '../bookings/types';

// Optimistic desk actions (Freddi, 2026-07-24). Clicking Arrived / Collected /
// Paid used to wait on the PATCH *and* the window reload before the row moved —
// two round trips of dead click, papered over with a brightness flash. The care
// tiles in the booking modal feel instant because they are plain local state,
// and that is the feel this restores: the row renders from an overlay applied
// the moment you click, which drops itself as soon as the server agrees.
//
// Pure and DOM-free — TodayView owns the state and the writes.

export interface PendingWrite {
  status?: BookingStatus;
  paymentStatus?: 'paid' | 'unpaid';
}

export type PendingMap = Record<string, PendingWrite>;

// The rows to render: the fetched bookings with any in-flight change laid over
// the top. Untouched rows keep their object identity (no needless re-render),
// and with nothing pending the original array comes straight back.
export function applyPending(bookings: Booking[], pending: PendingMap): Booking[] {
  if (Object.keys(pending).length === 0) return bookings;
  return bookings.map((b) => {
    const p = pending[b.id];
    if (!p) return b;
    return { ...b, ...(p.status ? { status: p.status } : {}), ...(p.paymentStatus ? { paymentStatus: p.paymentStatus } : {}) };
  });
}

// Drop ONE field of a row's overlay — the field owned by a write that just
// failed — leaving any concurrent field in place. A status write failing must
// not also roll back a payment toggle the user made on the same row while the
// first was still in flight (PR #147 review). Returns the same object when
// there is nothing to drop, and removes the row entry entirely once empty.
export function clearPendingField(pending: PendingMap, id: string, field: keyof PendingWrite): PendingMap {
  const entry = pending[id];
  if (!entry || entry[field] === undefined) return pending;
  const { [field]: _dropped, ...rest } = entry;
  const next = { ...pending };
  if (Object.keys(rest).length === 0) delete next[id];
  else next[id] = rest;
  return next;
}

// Drop every overlay the server has caught up with. Called whenever a fresh
// `bookings` set lands: an entry survives only while the fetched row still
// disagrees with it, which is exactly what stops the row flicking back to its
// old value between the PATCH landing and the reload arriving. Returns the SAME
// object when nothing settles, so it can be fed straight to setState without
// churning renders.
export function settlePending(bookings: Booking[], pending: PendingMap): PendingMap {
  const ids = Object.keys(pending);
  if (ids.length === 0) return pending;

  const settled: string[] = [];
  for (const b of bookings) {
    const p = pending[b.id];
    if (!p) continue;
    const statusAgreed = p.status === undefined || b.status === p.status;
    const paymentAgreed = p.paymentStatus === undefined || derivePaymentStatus(b) === p.paymentStatus;
    if (statusAgreed && paymentAgreed) settled.push(b.id);
  }
  if (settled.length === 0) return pending;

  const next: PendingMap = {};
  for (const id of ids) if (!settled.includes(id)) next[id] = pending[id]!;
  return next;
}
